import React, { useState, useEffect } from 'react';
import { X, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';

export const BriefingModal = ({ isOpen, onClose, inquiryType }) => {
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    firm: '',
    name: '',
    email: '',
    inquiry: inquiryType || '',
  });

  useEffect(() => {
    if (isOpen) {
      setStatus(null);
      setSending(false);
      setForm((f) => ({ ...f, inquiry: inquiryType || '' }));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, inquiryType]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_LANDING,
        {
          firm_name: form.firm,
          partner_name: form.name,
          work_email: form.email,
          inquiry_type: form.inquiry,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('sent');
      setTimeout(() => {
        onClose();
        setForm({ firm: '', name: '', email: '', inquiry: '' });
        setStatus(null);
      }, 3000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  const inquiryOptions = [
    'Platform Demo & Capabilities Briefing',
    'Live Transaction Mandate',
    'Co-Design Partnership',
    'Sovereignty & Deployment Architecture',
    'General Inquiry',
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-slate-700/75 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-white/40 hover:text-white transition z-10"
        >
          <X size={20} />
        </button>

        {status !== 'sent' ? (
          <div className="p-8 md:p-10">
            <div className="mb-7">
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 font-medium mb-3">
                Confidential Intake
              </p>
              <h3 className="text-2xl font-bold text-white tracking-tight">
                Request a Briefing
              </h3>
              <p className="text-[13px] text-white/45 mt-1.5 leading-relaxed">
                Frontier engagements begin with a private briefing scoped to your mandate.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">
                  Firm / Institution
                </label>
                <input
                  type="text"
                  required
                  value={form.firm}
                  onChange={(e) => setForm({ ...form, firm: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder-white/25 focus:outline-none focus:border-white/25 transition"
                  placeholder="e.g. Mubadala, Bain Capital, McKinsey"
                  disabled={sending}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">
                  Lead Partner Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder-white/25 focus:outline-none focus:border-white/25 transition"
                  placeholder="Full name"
                  disabled={sending}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">
                  Work Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder-white/25 focus:outline-none focus:border-white/25 transition"
                  placeholder="partner@firm.com"
                  disabled={sending}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5 uppercase tracking-wide">
                  Nature of Inquiry
                </label>
                <select
                  required
                  value={form.inquiry}
                  onChange={(e) => setForm({ ...form, inquiry: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white focus:outline-none focus:border-white/25 transition appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23ffffff' viewBox='0 0 24 24'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  disabled={sending}
                >
                  <option value="" disabled className="bg-slate-700">Select inquiry type</option>
                  {inquiryOptions.map((opt) => (
                    <option key={opt} value={opt} className="bg-slate-700">{opt}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#1e1e1e] font-semibold text-sm rounded-lg px-6 py-3.5 transition-colors mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending...</>
                ) : (
                  <>Submit Inquiry <ArrowRight size={16} /></>
                )}
              </button>

              {status === 'error' && (
                <p className="text-xs text-red-400 text-center">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>

            <p className="text-[10px] text-white/20 text-center mt-5">
              Protected under Frontier's Zero-Disclosure Protocol. No data is shared externally.
            </p>
          </div>
        ) : (
          <div className="p-8 flex flex-col items-center text-center py-16">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-5">
              <CheckCircle2 size={28} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 tracking-tight">Inquiry Received</h3>
            <p className="text-sm text-white/45 max-w-xs leading-relaxed">
              A Frontier partner will respond within 24 hours to schedule your confidential briefing.
            </p>
            <button
              onClick={onClose}
              className="mt-8 text-sm font-medium text-white/60 hover:text-white transition"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
